"use client";
import { useCallback, useMemo } from "react";
import { isMobile } from "@layerswap/utils";
import { useSettingsState } from "../context/settings";
import { useWalletProviderSnapshots } from "@layerswap/wallet-core";
export default function useWallet(network, purpose) {
    const allSnapshots = useWalletProviderSnapshots();
    const { networks } = useSettingsState();
    const isMobilePlatform = isMobile();
    const walletProviders = useMemo(() => allSnapshots.filter(provider => (isMobilePlatform ? !provider.unsupportedPlatforms?.includes('mobile') : !provider.unsupportedPlatforms?.includes('desktop')) &&
        networks.some(net => provider.autofillSupportedNetworks?.includes(net.name) ||
            provider.withdrawalSupportedNetworks?.includes(net.name) ||
            provider.asSourceSupportedNetworks?.includes(net.name))), [allSnapshots, networks, isMobilePlatform]);
    const provider = useMemo(() => network && resolveProvider(network, walletProviders, purpose), [network, purpose, walletProviders]);
    const wallets = useMemo(() => {
        let connectedWallets = [];
        walletProviders.forEach((provider) => {
            const w = provider.connectedWallets?.map(wallet => {
                return resolveWallet(wallet, network, provider, purpose);
            });
            connectedWallets = w ? [...connectedWallets, ...w] : [...connectedWallets];
        });
        return connectedWallets;
    }, [walletProviders, network]);
    const unAvailableWallets = useMemo(() => {
        return wallets.filter(wallet => wallet.isNotAvailable);
    }, [wallets]);
    const availableWallets = useMemo(() => {
        return wallets.filter(wallet => !wallet.isNotAvailable);
    }, [wallets]);
    const getProvider = useCallback((network, purpose) => {
        return network && resolveProvider(network, walletProviders, purpose);
    }, [walletProviders]);
    const res = useMemo(() => ({
        wallets: availableWallets,
        unAvailableWallets,
        provider,
        providers: walletProviders,
        getProvider
    }), [availableWallets, unAvailableWallets, provider, walletProviders, getProvider]);
    return res;
}
// When `isNotAvailableCondition` is set, `resolveProvider` builds a fresh
// wrapper object (and a fresh `requestAdditionalConnectors` closure) on every
// call. Because `useWallet` recomputes whenever ANY provider's snapshot moves
// (the snapshot array changes identity even if the resolved provider didn't),
// that would hand every consumer a new `provider` reference on unrelated
// updates and cascade re-renders. Cache the wrapper keyed weakly by the base
// snapshot object so the same (snapshot, network, purpose) returns a stable
// reference; a real state change produces a new snapshot → new cache entry.
const resolvedProviderCache = new WeakMap();
const resolveProvider = (network, walletProviders, purpose) => {
    if (!purpose || !network)
        return;
    let provider = undefined;
    switch (purpose) {
        case "withdrawal":
            provider = walletProviders.find(provider => provider.withdrawalSupportedNetworks?.includes(network.name));
            break;
        case "autofill":
            provider = walletProviders.find(provider => provider.autofillSupportedNetworks?.includes(network.name));
            break;
        case "asSource":
            provider = walletProviders.find(provider => provider.asSourceSupportedNetworks?.includes(network.name));
            break;
    }
    if (provider?.isNotAvailableCondition && purpose) {
        const cacheKey = `${network.name}|${purpose}`;
        const cachedByKey = resolvedProviderCache.get(provider);
        const cached = cachedByKey?.get(cacheKey);
        if (cached)
            return cached;
        const availableConnectors = provider.availableConnectors?.filter(connector => (provider.isNotAvailableCondition && network?.name) ? !provider.isNotAvailableCondition(connector.id, network?.name, purpose) : true);
        const additionalConnectors = provider.additionalConnectors?.filter(connector => (provider.isNotAvailableCondition && network?.name) ? !provider.isNotAvailableCondition(connector.id, network?.name, purpose) : true);
        const requestAdditionalConnectors = provider.requestAdditionalConnectors
            ? async (params) => {
                const result = await provider.requestAdditionalConnectors?.(params);
                if (!result) {
                    return { connectors: [], nextPage: null, totalCount: 0 };
                }
                return {
                    ...result,
                    connectors: result.connectors.filter(connector => (provider.isNotAvailableCondition && network?.name) ? !provider.isNotAvailableCondition(connector.id, network?.name, purpose) : true)
                };
            }
            : undefined;
        const resolvedProvider = {
            ...provider,
            connectedWallets: provider.connectedWallets?.map(wallet => {
                const connectorId = wallet.internalId ?? wallet.id;
                return {
                    ...wallet,
                    isNotAvailable: (provider.isNotAvailableCondition && network?.name && connectorId) ? provider.isNotAvailableCondition(connectorId, network?.name, purpose) : false,
                };
            }),
            activeWallet: provider.activeWallet ? {
                ...provider.activeWallet,
                isNotAvailable: (network?.name) ? provider.isNotAvailableCondition(provider.activeWallet.id, network?.name, purpose) : false,
            } : undefined,
            availableConnectors: availableConnectors,
            additionalConnectors,
            requestAdditionalConnectors,
        };
        const byKey = cachedByKey ?? new Map();
        byKey.set(cacheKey, resolvedProvider);
        if (!cachedByKey)
            resolvedProviderCache.set(provider, byKey);
        return resolvedProvider;
    }
    return provider;
};
const resolveWallet = (wallet, network, provider, purpose) => {
    if (provider.isNotAvailableCondition && network?.name && wallet.internalId && !purpose) {
        return {
            ...wallet,
            isNotAvailable: provider.isNotAvailableCondition(wallet.internalId, network?.name),
        };
    }
    if (purpose === "autofill") {
        return {
            ...wallet,
            isNotAvailable: !wallet.autofillSupportedNetworks?.some(n => n.toLowerCase() === network?.name.toLowerCase()),
        };
    }
    else if (purpose === "withdrawal") {
        return {
            ...wallet,
            isNotAvailable: !wallet.withdrawalSupportedNetworks?.some(n => n.toLowerCase() === network?.name.toLowerCase()),
        };
    }
    else if (purpose === "asSource") {
        return {
            ...wallet,
            isNotAvailable: !wallet.asSourceSupportedNetworks?.some(n => n.toLowerCase() === network?.name.toLowerCase()),
        };
    }
    return {
        ...wallet,
        isNotAvailable: false,
    };
};
